// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Turning a pasted chat answer into a list of package names.
 *
 * This is the part that decides whether the tool is trustworthy, and almost all
 * of the work is in NOT reporting things. A checker that asks PyPI whether
 * `os`, `./utils`, `node:fs` or `your-api-key-here` exists will tell the user
 * that four of their imports are hallucinations, and they will close the tab.
 *
 * Every exclusion is recorded with its reason and shown in the interface, so
 * "ghostdep ignored this" is visible rather than silent. A tool that quietly
 * drops half the input looks identical to one that found nothing wrong.
 */

import { NODE_BUILTINS, PYTHON_STDLIB } from './data/names.generated.ts';
import { normalisePypi } from './distance.ts';
import { resolveNpmSpecifier, resolvePythonImport } from './importmap.ts';
import type { Candidate, Ecosystem, ExtractResult, Excluded, Origin } from './types.ts';

const NODE_BUILTIN_SET = new Set(NODE_BUILTINS);
const PYTHON_STDLIB_SET = new Set(PYTHON_STDLIB);

/* ── name legality ────────────────────────────────────────────────────────
 * An illegal name is not a missing package, it is not a package at all, and
 * the difference matters: `Your-API-Key` reported as "does not exist in the
 * registry" reads as a finding when it is a placeholder. */

const NPM_NAME = /^(?:@[a-z0-9][a-z0-9._-]*\/)?[a-z0-9][a-z0-9._-]*$/;
const PYPI_NAME = /^[A-Za-z0-9](?:[A-Za-z0-9._-]*[A-Za-z0-9])?$/;

export function isLegalName(name: string, ecosystem: Ecosystem): boolean {
  if (!name || name.length > 214) return false;
  return ecosystem === 'npm' ? NPM_NAME.test(name) : PYPI_NAME.test(name);
}

/* ── placeholders ─────────────────────────────────────────────────────────
 * Assistants write these constantly. They are not hallucinated packages and
 * must never be counted as one. */
const PLACEHOLDER = /^(your|my|the|some|example|sample|placeholder|todo|xxx|foo|bar|baz|package|module|library|name)([-_.].*)?$/i;

/* ── conda ────────────────────────────────────────────────────────────────
 * `conda install x` resolves against Anaconda's channels, not PyPI. Names
 * frequently differ (`pytorch` on conda-forge is `torch` on PyPI), so checking
 * a conda name against PyPI would produce confident nonsense in both
 * directions. It is excluded by name, with the reason stated. */

interface Sink {
  readonly candidates: Candidate[];
  readonly excluded: Excluded[];
  readonly seen: Set<string>;
}

function pushCandidate(
  sink: Sink,
  ecosystem: Ecosystem,
  raw: string,
  name: string,
  alternatives: readonly string[],
  origin: Origin,
  evidence: string,
  line: number,
  declared: string | null,
  mappingNote: string | null,
): void {
  // PEP 503: `langchain_communty` and `langchain-communty` are the SAME PyPI
  // project, so deduplicating on a lowercased raw string reports one package
  // twice — once from the install command and once from the import — and the
  // headline count is then wrong in the direction that most damages trust.
  // Measured on the shipped sample: 10 "does not exist" findings for 7 real
  // ones.
  const key = `${ecosystem}:${ecosystem === 'pypi' ? normalisePypi(name) : name.toLowerCase()}`;
  if (sink.seen.has(key)) return;
  sink.seen.add(key);
  sink.candidates.push({
    ecosystem,
    raw,
    name,
    alternatives,
    origin,
    evidence: evidence.trim().slice(0, 200),
    line,
    declared,
    mappingNote,
  });
}

function exclude(sink: Sink, raw: string, reason: string, line: number): void {
  const key = `x:${raw}:${reason}`;
  if (sink.seen.has(key)) return;
  sink.seen.add(key);
  sink.excluded.push({ raw, reason, line });
}

/** Shared gate for anything about to be treated as a real package name. */
function admit(
  sink: Sink,
  ecosystem: Ecosystem,
  raw: string,
  origin: Origin,
  evidence: string,
  line: number,
  declared: string | null,
): void {
  if (ecosystem === 'npm') {
    const root = resolveNpmSpecifier(raw);
    if (root === null) {
      exclude(sink, raw, 'not a package specifier (relative path, URL or protocol import)', line);
      return;
    }
    if (root.startsWith('node:') || NODE_BUILTIN_SET.has(root)) {
      exclude(sink, root, 'Node.js builtin', line);
      return;
    }
    if (PLACEHOLDER.test(root)) {
      exclude(sink, root, 'looks like a placeholder, not a real name', line);
      return;
    }
    if (!isLegalName(root, 'npm')) {
      exclude(sink, root, 'not a legal npm package name', line);
      return;
    }
    pushCandidate(sink, 'npm', raw, root, [], origin, evidence, line, declared, null);
    return;
  }

  const bare = raw.replace(/\[.*?\]$/, '');
  const top = bare.split('.')[0];
  // THE IMPORT MAP ONLY APPLIES TO IMPORTS. `pip install flask` is the user
  // writing a DISTRIBUTION name; rewriting it to `Flask` because that is the
  // canonical casing changes what they wrote to no purpose, since PEP 503
  // makes the two the same project. Worse, it puts a "← flask" substitution
  // badge on a row where nothing was substituted, which is exactly the kind of
  // noise that makes people stop believing the badges that matter.
  const resolution =
    origin === 'import'
      ? resolvePythonImport(bare)
      : { importName: bare, candidates: [bare], note: null, substituted: false };
  if (PYTHON_STDLIB_SET.has(top)) {
    exclude(sink, top, 'Python standard library', line);
    return;
  }
  if (PLACEHOLDER.test(top)) {
    exclude(sink, top, 'looks like a placeholder, not a real name', line);
    return;
  }
  if (resolution.candidates.length === 0) {
    exclude(sink, bare, resolution.note ?? 'a namespace package with no single distribution', line);
    return;
  }
  const [primary, ...alternatives] = resolution.candidates;
  if (!isLegalName(primary, 'pypi')) {
    exclude(sink, primary, 'not a legal PyPI project name', line);
    return;
  }
  pushCandidate(
    sink,
    'pypi',
    raw,
    primary,
    alternatives,
    origin,
    evidence,
    line,
    declared,
    resolution.substituted ? resolution.note : null,
  );
}

/* ── manifests ────────────────────────────────────────────────────────────── */

const DEP_FIELDS = ['dependencies', 'devDependencies', 'peerDependencies', 'optionalDependencies'];

function fromPackageJson(sink: Sink, text: string, notes: string[]): boolean {
  let doc: unknown;
  try {
    doc = JSON.parse(text);
  } catch {
    return false;
  }
  if (typeof doc !== 'object' || doc === null) return false;
  const record = doc as Record<string, unknown>;
  if (!DEP_FIELDS.some((f) => typeof record[f] === 'object' && record[f] !== null)) return false;

  for (const field of DEP_FIELDS) {
    const block = record[field];
    if (typeof block !== 'object' || block === null) continue;
    for (const [name, range] of Object.entries(block as Record<string, unknown>)) {
      const spec = typeof range === 'string' ? range : '';
      // `workspace:*`, `file:../x`, `link:`, `git+https://…` and `npm:alias@1`
      // are not registry lookups. The alias form is the interesting one: the
      // name on the left is local, and the package that actually gets
      // installed is on the right.
      if (/^(workspace|file|link|portal):/.test(spec)) {
        exclude(sink, name, `local dependency (${spec.split(':')[0]}:)`, 0);
        continue;
      }
      if (/^(git\+|https?:|github:|bitbucket:|gitlab:)/.test(spec)) {
        exclude(sink, name, 'installed from a URL, not from the registry', 0);
        continue;
      }
      const alias = /^npm:(@?[^@]+(?:\/[^@]+)?)@?/.exec(spec);
      if (alias) {
        admit(sink, 'npm', alias[1], 'manifest', `"${name}": "${spec}"`, 0, spec);
        continue;
      }
      admit(sink, 'npm', name, 'manifest', `"${name}": "${spec}"`, 0, spec || null);
    }
  }
  notes.push('Read as package.json.');
  return true;
}

/** One requirements.txt line. Exported for the tests — this grammar has teeth. */
export function parseRequirementLine(line: string): { name: string; declared: string | null } | null {
  // Strip a trailing comment, but only when the `#` starts a token: a URL
  // fragment `#egg=name` and `#sha256=...` are part of the line.
  const withoutComment = line.replace(/(^|\s)#(?!egg=|sha256=).*$/, '').trim();
  if (!withoutComment) return null;
  // Environment markers and hashes are constraints on an existing name.
  const beforeMarker = withoutComment.split(';')[0].trim();
  const beforeHash = beforeMarker.split(/\s--hash[=\s]/)[0].trim();
  if (!beforeHash) return null;
  const match = /^([A-Za-z0-9][A-Za-z0-9._-]*)\s*(\[[^\]]*\])?\s*(.*)$/.exec(beforeHash);
  if (!match) return null;
  const rest = match[3].trim();
  // `name @ https://…` is a direct reference, not a registry lookup.
  if (rest.startsWith('@')) return null;
  if (rest && !/^[<>=!~]/.test(rest)) return null;
  return { name: match[1], declared: rest || null };
}

function fromRequirements(sink: Sink, text: string, notes: string[]): boolean {
  const lines = text.split('\n');
  let hits = 0;
  lines.forEach((line, index) => {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) return;
    if (/^-(-)?(r|requirement|c|constraint)\b/.test(trimmed)) {
      notes.push(`Line ${index + 1} includes another file (${trimmed}); ghostdep only sees what you paste.`);
      return;
    }
    if (/^-(-)?e\b/.test(trimmed) || /^(https?|git\+|file):/.test(trimmed)) {
      exclude(sink, trimmed.slice(0, 60), 'installed from a URL or an editable path', index + 1);
      return;
    }
    if (trimmed.startsWith('-')) return;
    const parsed = parseRequirementLine(trimmed);
    if (!parsed) return;
    hits++;
    admit(sink, 'pypi', parsed.name, 'requirements', trimmed, index + 1, parsed.declared);
  });
  if (hits) notes.push('Read as a requirements file.');
  return hits > 0;
}

/* ── install commands and imports in prose ────────────────────────────────── */

const NPM_INSTALL = /\b(?:npm|pnpm|bun)\s+(?:install|i|add)\s+([^\n&|;]+)/g;
const YARN_ADD = /\byarn\s+add\s+([^\n&|;]+)/g;
const PIP_INSTALL = /\b(?:pip3?|uv\s+pip|python3?\s+-m\s+pip)\s+install\s+([^\n&|;]+)/g;
const POETRY_ADD = /\bpoetry\s+add\s+([^\n&|;]+)/g;
const CONDA_INSTALL = /\bconda\s+install\s+([^\n&|;]+)/g;

/** Strip flags and version pins from an install command's argument list. */
function installArguments(rest: string): string[] {
  return rest
    .split(/\s+/)
    .filter((token) => token && !token.startsWith('-'))
    .map((token) => token.replace(/^["']|["']$/g, ''));
}

function stripNpmVersion(token: string): string {
  // `@scope/name@1.2.3` — the version separator is the LAST `@`, and only when
  // it is not the leading scope marker. Splitting on the first `@` turns every
  // scoped package into an empty name.
  const at = token.lastIndexOf('@');
  if (at > 0) return token.slice(0, at);
  return token;
}

function stripPypiVersion(token: string): string {
  return token.split(/[<>=!~[]/)[0];
}

// Everything after `import`, so an `as` alias inside a comma list survives:
// `import numpy as np, pandas` is two imports, and a pattern that stops at the
// first alias silently loses every name after it.
const PY_IMPORT = /^\s*import\s+([A-Za-z_][\w.]*(?:\s+as\s+\w+)?(?:\s*,\s*[A-Za-z_][\w.]*(?:\s+as\s+\w+)?)*)/;
const PY_FROM = /^\s*from\s+([A-Za-z_][\w.]*)\s+import\s/;
const JS_FROM = /(?:^|[\s;}])(?:import|export)\s[^'"]*from\s*['"]([^'"]+)['"]/g;
const JS_BARE_IMPORT = /(?:^|[\s;])import\s*['"]([^'"]+)['"]/g;
const JS_REQUIRE = /\brequire\s*\(\s*['"]([^'"]+)['"]\s*\)/g;
const JS_DYNAMIC = /\bimport\s*\(\s*['"]([^'"]+)['"]\s*\)/g;

/**
 * Which ecosystem a fenced block belongs to. `null` means undecided, and the
 * line syntax decides instead — a chat answer routinely has unlabelled fences.
 */
function fenceEcosystem(info: string): Ecosystem | null {
  const lang = info.trim().toLowerCase().split(/[\s:{]/)[0];
  if (['py', 'python', 'python3', 'ipython', 'jupyter'].includes(lang)) return 'pypi';
  if (['js', 'jsx', 'ts', 'tsx', 'javascript', 'typescript', 'node', 'mjs', 'cjs', 'vue', 'svelte'].includes(lang)) {
    return 'npm';
  }
  return null;
}

function scanText(sink: Sink, text: string): void {
  const lines = text.split('\n');
  let fence: string | null = null;
  let fenceLang: Ecosystem | null = null;

  lines.forEach((line, index) => {
    const lineNumber = index + 1;
    const fenceMatch = /^\s*(`{3,}|~{3,})(.*)$/.exec(line);
    if (fenceMatch) {
      if (fence && fenceMatch[1].startsWith(fence[0]) && fenceMatch[1].length >= fence.length) {
        fence = null;
        fenceLang = null;
      } else if (!fence) {
        fence = fenceMatch[1];
        fenceLang = fenceEcosystem(fenceMatch[2]);
      }
      return;
    }

    /* install commands — these carry their own ecosystem, wherever they appear */
    for (const [pattern, ecosystem] of [
      [NPM_INSTALL, 'npm'],
      [YARN_ADD, 'npm'],
    ] as const) {
      pattern.lastIndex = 0;
      let m: RegExpExecArray | null;
      while ((m = pattern.exec(line))) {
        for (const token of installArguments(m[1])) {
          admit(sink, ecosystem, stripNpmVersion(token), 'install-command', line, lineNumber, null);
        }
      }
    }
    for (const pattern of [PIP_INSTALL, POETRY_ADD]) {
      pattern.lastIndex = 0;
      let m: RegExpExecArray | null;
      while ((m = pattern.exec(line))) {
        for (const token of installArguments(m[1])) {
          // ANCHORED TO THE SCHEME SEPARATOR, not to the letters. `/^https?/`
          // matches `httpx` — one of the most-installed packages on PyPI —
          // and drops it as a URL, silently, with a reason that reads as
          // deliberate. Same for `/^\./` against a name that merely contains a
          // dot, and `/^git\+/` is fine only because `+` cannot start a name.
          if (/^(https?:\/\/|git\+|file:|\.{1,2}\/|\/)/.test(token)) {
            exclude(sink, token.slice(0, 60), 'installed from a URL or a path', lineNumber);
            continue;
          }
          admit(sink, 'pypi', stripPypiVersion(token), 'install-command', line, lineNumber, null);
        }
      }
    }
    CONDA_INSTALL.lastIndex = 0;
    let conda: RegExpExecArray | null;
    while ((conda = CONDA_INSTALL.exec(line))) {
      for (const token of installArguments(conda[1])) {
        // Not checked against PyPI on purpose: conda channels are a different
        // namespace and the names genuinely differ (conda's `pytorch` is
        // PyPI's `torch`), so a lookup here would be wrong in both directions.
        exclude(sink, stripPypiVersion(token), 'conda channel package — a different registry from PyPI', lineNumber);
      }
    }

    /* imports */
    const isJsLine = /\bfrom\s*['"]|require\s*\(|import\s*['"]|import\s*\{/.test(line);
    const ecosystem: Ecosystem | null = isJsLine ? 'npm' : fenceLang;

    if (ecosystem !== 'pypi') {
      for (const pattern of [JS_FROM, JS_BARE_IMPORT, JS_REQUIRE, JS_DYNAMIC]) {
        pattern.lastIndex = 0;
        let m: RegExpExecArray | null;
        while ((m = pattern.exec(line))) admit(sink, 'npm', m[1], 'import', line, lineNumber, null);
      }
    }

    if (ecosystem !== 'npm') {
      const from = PY_FROM.exec(line);
      if (from) {
        if (from[1].startsWith('.')) exclude(sink, from[1], 'relative import', lineNumber);
        else admit(sink, 'pypi', from[1], 'import', line, lineNumber, null);
        return;
      }
      const imports = PY_IMPORT.exec(line);
      // `import x from 'y'` is JavaScript and matches PY_IMPORT's first token
      // too, so the JS shape is excluded explicitly rather than by ordering.
      if (imports && !/\bfrom\s*['"]/.test(line)) {
        for (const part of imports[1].split(',')) {
          const name = part.trim().split(/\s+as\s+/)[0].trim();
          if (name) admit(sink, 'pypi', name, 'import', line, lineNumber, null);
        }
      }
    }
  });
}

/* ── entry point ──────────────────────────────────────────────────────────── */

export function extract(text: string, filename?: string): ExtractResult {
  const sink: Sink = { candidates: [], excluded: [], seen: new Set() };
  const notes: string[] = [];

  const looksLikeManifest = /package\.json$/i.test(filename ?? '');
  const looksLikeRequirements = /(requirements[\w.-]*\.txt|constraints[\w.-]*\.txt)$/i.test(filename ?? '');

  if (looksLikeManifest || (!filename && fromPackageJson(sink, text, notes))) {
    if (looksLikeManifest && !sink.candidates.length) fromPackageJson(sink, text, notes);
    return { candidates: sink.candidates, excluded: sink.excluded, notes };
  }
  if (looksLikeRequirements) {
    fromRequirements(sink, text, notes);
    return { candidates: sink.candidates, excluded: sink.excluded, notes };
  }

  if (/^\s*\[(project|tool\.poetry)]/m.test(text)) {
    notes.push(
      'This looks like a pyproject.toml. ghostdep does not parse TOML in this version — paste the dependency list itself and it will read those lines.',
    );
  }

  // A bare requirements-shaped paste (every non-empty line is `name==version`)
  // is common and worth recognising before falling through to the text scan.
  const requirementish = text
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l && !l.startsWith('#'));
  const pinned = requirementish.filter((l) => /^[A-Za-z0-9][A-Za-z0-9._-]*\s*(\[[^\]]*\])?\s*[<>=!~]/.test(l));
  if (requirementish.length > 0 && pinned.length >= Math.max(2, requirementish.length * 0.6)) {
    fromRequirements(sink, text, notes);
    if (sink.candidates.length) return { candidates: sink.candidates, excluded: sink.excluded, notes };
  }

  scanText(sink, text);
  return { candidates: sink.candidates, excluded: sink.excluded, notes };
}
